import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGameStore } from '../store/gameStore';
import { useTranslation } from '../hooks/useTranslation';

interface TutorialStep {
    id: string;
    title: string;
    description: string;
    top: string;
    left: string;
}

interface BaseTutorialProps {
    onEnd: () => void;
}

export const BaseTutorial: React.FC<BaseTutorialProps> = ({ onEnd }) => {
    const t = useTranslation();
    const [currentStep, setCurrentStep] = useState(0);
    const skipAllTutorials = useGameStore(state => state.skipAllTutorials);

    const handleSkipTutorial = () => {
        skipAllTutorials();
        // Не вызываем onEnd(), так как skipAllTutorials() уже устанавливает hasSeenBaseTutorial: true
        // и мы не хотим запускать расширенный туториал
    };

    const steps: TutorialStep[] = [
        {
            id: 'barracks',
            title: t.tutorial.base.barracks.title,
            description: t.tutorial.base.barracks.description,
            top: '47%',
            left: '32%'
        },
        {
            id: 'raid',
            title: t.tutorial.base.raid.title,
            description: t.tutorial.base.raid.description,
            top: '65%',
            left: '43%'
        },
        {
            id: 'forge',
            title: t.tutorial.base.forge.title,
            description: t.tutorial.base.forge.description,
            top: '49%',
            left: '16%'
        },
        {
            id: 'temple',
            title: t.tutorial.base.temple.title,
            description: t.tutorial.base.temple.description,
            top: '47%',
            left: '70%'
        },
        {
            id: 'mageTower',
            title: t.tutorial.base.mageTower.title,
            description: t.tutorial.base.mageTower.description,
            top: '40%',
            left: '84%'
        },
        {
            id: 'castle',
            title: t.tutorial.base.castle.title,
            description: t.tutorial.base.castle.description,
            top: '38%',
            left: '55%'
        },
        {
            id: 'taxHouse',
            title: t.tutorial.base.taxHouse.title,
            description: t.tutorial.base.taxHouse.description,
            top: '80%',
            left: '85%'
        },
        {
            id: 'orcAttack',
            title: t.tutorial.base.orcAttack.title,
            description: t.tutorial.base.orcAttack.description,
            top: '80%',
            left: '10%'
        },
        {
            id: 'all',
            title: t.tutorial.base.all.title,
            description: t.tutorial.base.all.description,
            top: '50%',
            left: '50%'
        }
    ];

    const handleNext = () => {
        if (currentStep < steps.length - 1) {
            setCurrentStep(currentStep + 1);
        } else {
            onEnd();
        }
    };

    const isFinalStep = currentStep === steps.length - 1;

    return (
        <div style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1000,
            pointerEvents: 'none',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
        }}>
            {/* Dark Overlay with Hole */}
            <div style={{
                position: 'absolute',
                inset: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                pointerEvents: 'auto',
                WebkitMaskImage: !isFinalStep || steps[currentStep].id !== 'all'
                    ? `radial-gradient(circle 80px at ${steps[currentStep].left} ${steps[currentStep].top}, transparent 100%, black 100%)`
                    : 'none',
                maskImage: !isFinalStep || steps[currentStep].id !== 'all'
                    ? `radial-gradient(circle 80px at ${steps[currentStep].left} ${steps[currentStep].top}, transparent 100%, black 100%)`
                    : 'none'
            }} />

            {/* Message Box */}
            <AnimatePresence mode="wait">
                <motion.div
                    key={currentStep}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    style={{
                        position: 'relative',
                        zIndex: 1001,
                        pointerEvents: 'auto',
                        backgroundColor: '#1c1917',
                        border: '2px solid #ca8a04',
                        padding: '1.5rem',
                        maxWidth: '450px',
                        width: '90%',
                        borderRadius: '0.5rem',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.8)',
                        textAlign: 'center',
                        fontFamily: '"Press Start 2P", cursive',
                        // Для шагов с нижними зданиями (дом старосты, нападение орков, ворота) показываем окно сверху
                        marginTop: (steps[currentStep].id === 'taxHouse' || steps[currentStep].id === 'orcAttack' || steps[currentStep].id === 'raid') ? '10%' : 'auto',
                        marginBottom: (steps[currentStep].id === 'taxHouse' || steps[currentStep].id === 'orcAttack' || steps[currentStep].id === 'raid') ? 'auto' : '10%'
                    }}
                >
                    <h3 style={{
                        color: '#fbbf24',
                        fontSize: '0.9rem',
                        marginBottom: '1rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.1em'
                    }}>
                        {steps[currentStep].title}
                    </h3>
                    <p style={{
                        color: '#fff',
                        fontSize: '0.65rem',
                        lineHeight: '1.6',
                        marginBottom: '1.5rem',
                        minHeight: '40px'
                    }}>
                        {steps[currentStep].description}
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={handleNext}
                            style={{
                                backgroundColor: isFinalStep ? '#059669' : '#ca8a04',
                                color: '#fff',
                                border: 'none',
                                padding: '0.75rem 1.5rem',
                                borderRadius: '0.25rem',
                                fontSize: '0.6rem',
                                cursor: 'pointer',
                                fontFamily: '"Press Start 2P", cursive',
                                boxShadow: isFinalStep ? '0 4px 0 #065f46' : '0 4px 0 #854d0e',
                                textTransform: 'uppercase',
                                width: '100%'
                            }}
                        >
                            {isFinalStep ? t.tutorial.base.all.title : t.tutorial.next}
                        </motion.button>
                        {currentStep === 0 && (
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={handleSkipTutorial}
                                style={{
                                    backgroundColor: 'transparent',
                                    color: '#9ca3af',
                                    border: '1px solid #4b5563',
                                    padding: '0.5rem 1rem',
                                    borderRadius: '0.25rem',
                                    fontSize: '0.5rem',
                                    cursor: 'pointer',
                                    fontFamily: '"Press Start 2P", cursive',
                                    textTransform: 'uppercase',
                                    width: '100%',
                                    opacity: 0.8
                                }}
                            >
                                {t.tutorial.skipTutorial}
                            </motion.button>
                        )}
                    </div>
                </motion.div>
            </AnimatePresence>
        </div>
    );
};
